import { formatAmount, formatAmountWithUnit } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@loot-os/ui/components/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";

import { FraudStateBadge } from "../components/FraudStateBadge";
import { KycStatusBadge } from "../components/KycStatusBadge";
import { PurchaseStatusBadge } from "../components/PurchaseStatusBadge";
import { ShortDate } from "../components/ShortDate";
import { TrustTierBadge } from "../components/TrustTierBadge";
import { amountColumnHeader, useCurrency } from "../lib/currency";
import { centsToDollars, formatDate, formatNumber } from "../lib/format";
import { lockReasonLabel } from "../lib/lockReasons";
import { useTRPC } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";

/**
 * High-level snapshot for the sheet. The full history (purchases, ledger,
 * opens) lives on the customer page at users/$userId — same users.get query,
 * so navigating there is a cache hit.
 */
function UserOverview({ endUserId, tenantId }: { endUserId: string; tenantId: string }) {
  const trpc = useTRPC();
  const currency = useCurrency();
  const detail = useQuery(trpc.users.get.queryOptions({ endUserId }));
  const data = detail.data;
  const lastPurchase = data?.purchases[0];
  const lastOpen = data?.opens[0];

  return (
    <div className="flex flex-col gap-4 overflow-y-auto px-4 pb-6">
      {detail.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {detail.error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {detail.error.message}
        </div>
      )}
      {data && (
        <>
          <Link
            to="/tenants/$tenantId/users/$userId"
            params={{ tenantId, userId: endUserId }}
            className={buttonVariants()}
          >
            View customer page
            <ArrowRight />
          </Link>

          <div className="grid grid-cols-2 gap-3">
            <Card className="gap-1 py-3">
              <CardHeader className="px-3">
                <CardDescription>External id</CardDescription>
              </CardHeader>
              <CardContent className="truncate px-3 font-mono text-xs">
                <span className="flex flex-wrap items-center gap-2">
                  {data.user.externalId}
                  {data.user.lockedAt && (
                    <Badge variant="destructive" title={lockReasonLabel(data.user.lockReasonCode)}>
                      locked
                    </Badge>
                  )}
                  <TrustTierBadge
                    tier={data.risk.tier}
                    reason={data.risk.reason}
                    overridden={data.risk.override !== null}
                  />
                  {data.risk.assessments[0] && (
                    <FraudStateBadge
                      state={data.risk.assessments[0].state}
                      source={data.risk.assessments[0].source}
                      title={`Latest screening: ${data.risk.assessments[0].action}`}
                    />
                  )}
                  {(data.kyc.required || data.kyc.status) && (
                    <KycStatusBadge
                      status={data.kyc.status}
                      verified={data.kyc.verifiedAt !== null}
                      policyIssue={data.kyc.latest?.policyIssue}
                    />
                  )}
                </span>
              </CardContent>
            </Card>
            <Card className="gap-1 py-3">
              <CardHeader className="px-3">
                <CardDescription>Balance</CardDescription>
              </CardHeader>
              <CardContent className="px-3 text-lg font-semibold tabular-nums">
                {formatAmountWithUnit(data.user.balanceCents, currency)}
              </CardContent>
            </Card>
            <Card className="gap-1 py-3">
              <CardHeader className="px-3">
                <CardDescription>Total opens</CardDescription>
              </CardHeader>
              <CardContent className="px-3 text-lg font-semibold tabular-nums">
                {formatNumber(data.user.nonce)}
              </CardContent>
            </Card>
            <Card className="gap-1 py-3">
              <CardHeader className="px-3">
                <CardDescription>Created</CardDescription>
              </CardHeader>
              <CardContent className="px-3 text-sm">{formatDate(data.user.createdAt)}</CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Stripe</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
              {!data.shopper ? (
                <p className="text-muted-foreground">
                  API-created user — no storefront shopper, so no Stripe customer.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-muted-foreground">Shopper email</span>
                    <span>{data.shopper.email}</span>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-muted-foreground">Last login</span>
                    <span>
                      {data.shopper.lastLoginAt ? formatDate(data.shopper.lastLoginAt) : "never"}
                    </span>
                  </div>
                  {data.stripeCustomers.length === 0 ? (
                    <p className="text-muted-foreground">
                      No Stripe customer yet — created at first checkout.
                    </p>
                  ) : (
                    data.stripeCustomers.map((c) => (
                      <div key={c.id} className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-muted-foreground">Customer</span>
                        <code className="font-mono text-xs">{c.stripeCustomerId}</code>
                        <span className="text-muted-foreground">on</span>
                        <code className="font-mono text-xs">{c.stripeAccountId}</code>
                      </div>
                    ))
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Latest activity</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-muted-foreground">Purchase</span>
                {lastPurchase ? (
                  <>
                    <span>{formatDate(lastPurchase.createdAt)}</span>
                    <span>{lastPurchase.packName}</span>
                    <span className="font-mono text-xs">
                      {centsToDollars(lastPurchase.amountCents)}
                    </span>
                    <PurchaseStatusBadge status={lastPurchase.status} />
                  </>
                ) : (
                  <span className="text-muted-foreground">none yet</span>
                )}
              </div>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-muted-foreground">Open</span>
                {lastOpen ? (
                  <>
                    <span>{formatDate(lastOpen.createdAt)}</span>
                    <span>{lastOpen.item.name}</span>
                    <Badge variant="outline">{formatAmount(lastOpen.item.value, currency)}</Badge>
                  </>
                ) : (
                  <span className="text-muted-foreground">none yet</span>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export function UsersPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  // ?user=<endUserId> deep-links straight into a user's detail sheet (e.g. from
  // the Payments page's customer column).
  const { user: userParam } = useSearch({ strict: false }) as { user?: string };
  const [selected, setSelected] = useState<string | null>(userParam ?? null);
  useEffect(() => {
    if (userParam) setSelected(userParam);
  }, [userParam]);
  const closeDetail = () => {
    setSelected(null);
    if (userParam) {
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, user: undefined }),
        replace: true,
      });
    }
  };
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search.trim());
  const currency = useCurrency();

  const users = useQuery(
    trpc.users.list.queryOptions(
      { limit: 50, search: debouncedSearch || undefined },
      // Keep the previous page visible while a new search resolves.
      { placeholderData: (previous) => previous },
    ),
  );
  // Placeholder data means the rows on screen belong to the previous search.
  const isStale = users.isPlaceholderData;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">End users</h1>
          <p className="text-sm text-muted-foreground">
            Created via your server through the public API or by the storefront. Adjust a balance
            from a customer's page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Clear and the hint sit left of the input so neither nudges the field. */}
          {isStale && <span className="text-sm text-muted-foreground">Searching…</span>}
          {search && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setSearch("")}>
              Clear
            </Button>
          )}
          <Input
            className="w-64"
            type="search"
            placeholder="Search id, external id, username or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <Card>
        <CardContent>
          <Table
            aria-busy={isStale}
            className={`transition-opacity ${isStale ? "opacity-60" : ""}`}
          >
            <TableHeader>
              <TableRow>
                <TableHead>External id</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Trust</TableHead>
                <TableHead>Fraud</TableHead>
                <TableHead>Identity</TableHead>
                <TableHead className="text-right">
                  {amountColumnHeader("Balance", currency)}
                </TableHead>
                <TableHead className="text-right">Opens (nonce)</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.isLoading && (
                <TableRow>
                  <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                    Loading end users…
                  </TableCell>
                </TableRow>
              )}
              {users.data?.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                    {debouncedSearch
                      ? `No end users match “${debouncedSearch}”.`
                      : "No end users yet — create them via POST /v1/users with your secret key."}
                  </TableCell>
                </TableRow>
              )}
              {users.data?.data.map((user) => (
                <TableRow
                  key={user.id}
                  className="cursor-pointer"
                  onClick={() => setSelected(user.id)}
                >
                  <TableCell className="font-mono text-xs">
                    <span className="flex items-center gap-2">
                      {user.externalId}
                      {user.lockedAt && (
                        <Badge variant="destructive" title={lockReasonLabel(user.lockReasonCode)}>
                          locked
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className={user.username ? undefined : "text-muted-foreground"}>
                    {user.username ?? "—"}
                  </TableCell>
                  <TableCell className={user.email ? undefined : "text-muted-foreground"}>
                    {user.email ?? "—"}
                  </TableCell>
                  <TableCell>
                    {user.trust ? (
                      <TrustTierBadge
                        tier={user.trust.tier}
                        reason={user.trust.reason}
                        overridden={user.trust.overridden}
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.lastAssessment ? (
                      <FraudStateBadge
                        state={user.lastAssessment.state}
                        source={user.lastAssessment.source}
                        title={`${user.lastAssessment.action} · ${formatDate(user.lastAssessment.createdAt)}`}
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.kycStatus || user.kycVerifiedAt ? (
                      <KycStatusBadge
                        status={user.kycStatus}
                        verified={user.kycVerifiedAt !== null}
                        title={
                          user.kycVerifiedAt
                            ? `Verified ${formatDate(user.kycVerifiedAt)}`
                            : undefined
                        }
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatAmount(user.balanceCents, currency)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">{user.nonce}</TableCell>
                  <TableCell className="text-muted-foreground">
                    <ShortDate value={user.createdAt} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Sheet
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) closeDetail();
        }}
      >
        <SheetContent className="w-full gap-0 overflow-y-auto data-[side=right]:sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>End user</SheetTitle>
            {selected && (
              <SheetDescription className="font-mono text-xs">{selected}</SheetDescription>
            )}
          </SheetHeader>
          {selected && <UserOverview endUserId={selected} tenantId={tenantId} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
